// 负责广告脚本加载和初始化
// 处理广告元素的可视区域检测(Intersection Observer)
// 管理广告加载重试机制
export default {
	data() {
		return {
			adObserver: null,
			domObserver: null,
			observedAds: new Set(),
			adLoadRetries: new Map(),
			maxRetries: 3,
			preloadDistance: 300, // 改为300px的预加载距离
		}
	},

	async mounted() {
		try {
			await this.initAdSystem()
			// If the component also uses adTrackingMixin, initAdTracking will be available.
			if (typeof this.initAdTracking === 'function') {
				this.$nextTick(() => {
					this.initAdTracking()
				})
			}
		} catch (error) {
			console.error(`[AdMixin] Ad system initialization error in ${this.$options.name}:`, error)
		}
	},
	beforeDestroy() {
		this.cleanupAdSystem()
	},
	methods: {
		async initAdSystem() {
			console.log(`[AdMixin] 🚀 初始化广告系统 | 组件: ${this.$options.name}`)
			if (typeof window !== 'undefined') {
				// 无论配置如何，直接加载 GPT
				await this.loadGptScript()
				
				this.$nextTick(() => {
					setTimeout(() => {
						this.setupAdObserver()
					}, 100)
				})
			}
		},

		async loadGptScript() {
			// Initialize GPT command queue immediately
			window.googletag = window.googletag || { cmd: [] }

			if (!window.googletag.apiReady) {
				try {
					// 强制使用 GPT 脚本 URL (也可以保留从配置读取，但确保是 GPT)
					// 如果配置没有，使用默认的 GPT 地址
					const scriptUrl = this.adsensConfig?.scriptUrl || "https://securepubads.g.doubleclick.net/tag/js/gpt.js"
					
					await this.loadScript(scriptUrl)
					this.$eventrack('adscript_add_success', 'expose')

					// Initialize GPT services globally once
					if (!window.gptServicesEnabled) {
						window.googletag.cmd.push(() => {
							// Define OutOfPage slots (Anchor & Interstitial)
							if (this.adsensConfig?.anchor) {
								const anchorConfig = this.adsensConfig.anchor
								console.log(`[AdMixin] 📣 GPT Define Anchor | Path: ${anchorConfig.path}`)
								const anchorSlot = window.googletag.defineOutOfPageSlot(
									anchorConfig.path,
									window.googletag.enums.OutOfPageFormat.BOTTOM_ANCHOR
								)
								if (anchorSlot) {
									anchorSlot.addService(window.googletag.pubads())
								}
							}

							if (this.adsensConfig?.interstitial) {
								const intConfig = this.adsensConfig.interstitial
								console.log(`[AdMixin] 📣 GPT Define Interstitial | Path: ${intConfig.path}`)
								const intSlot = window.googletag.defineOutOfPageSlot(
									intConfig.path,
									window.googletag.enums.OutOfPageFormat.INTERSTITIAL
								)
								if (intSlot) {
									intSlot.addService(window.googletag.pubads())
								}
							}

							window.googletag.pubads().enableSingleRequest()
							window.googletag.enableServices()
							window.gptServicesEnabled = true
						})
					}
				} catch (error) {
					console.error('Failed to load GPT script:', error)
					this.$eventrack('adscript_load_error', 'error')
				}
			} else {
				this.$eventrack('adscript_loaded', 'expose')
			}
		},

		loadScript(url) {
			return new Promise((resolve, reject) => {
				const script = document.createElement('script')
				script.src = url
				script.async = true
				script.crossOrigin = 'anonymous'
				script.onload = resolve
				script.onerror = reject
				document.head.appendChild(script)
			})
		},

		setupAdObserver() {
			if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
				return
			}

			if (this.adObserver) {
				this.adObserver.disconnect()
			}

			this.observedAds = new Set()

			console.log(`[AdMixin] 👀 启动监听 | 预加载: ${this.preloadDistance}px`)

		this.adObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach(this.handleAdIntersection)
			},
			{
				rootMargin: `${this.preloadDistance}px 0px ${this.preloadDistance}px 0px`,
				threshold: 0,
			},
		)

			const debounce = (func, delay) => {
				let timeout
				return (...args) => {
					clearTimeout(timeout)
					timeout = setTimeout(() => {
						func.apply(this, args)
					}, delay)
				}
			}
			const debouncedObserveAds = debounce(this.observeAds, 300)

			this.$nextTick(() => {
				setTimeout(() => {
					this.observeAds()
				}, 200)

				if (typeof MutationObserver !== 'undefined' && !this.domObserver) {
					this.domObserver = new MutationObserver(() => {
						debouncedObserveAds()
					})

					this.domObserver.observe(this.$el, {
						childList: true,
						subtree: true,
					})
				}
			})
		},

		observeAds() {
			// Only find GPT ads
			const adElements = this.$el.querySelectorAll('.gpt-ad');
			const newAdsCount = Array.from(adElements).filter(el => !this.observedAds.has(el)).length;

			if (newAdsCount > 0) {
				console.log(`[AdMixin] 🔍 发现 ${newAdsCount} 个新广告元素 (总数: ${adElements.length})`);
      }
      
      console.log(adElements);

			// 检查 adObserver 是否已初始化
			if (!this.adObserver) {
				console.warn('[AdMixin] ⚠️ adObserver 尚未初始化，跳过监听');
				return;
			}

			if (adElements.length > 0) {
				adElements.forEach((adElement) => {
					if (!adElement.getAttribute('data-ad-status') && !this.observedAds.has(adElement)) {
						const slot = adElement.getAttribute('data-ad-slot')
						console.log(`[AdMixin] ➕ 添加监听 | Slot: ${slot}`);

						// Define the slot immediately upon discovery
						this.defineGptSlot(adElement);

						this.observedAds.add(adElement);
						this.adObserver.observe(adElement);
					}
				});
			}
		},

	handleAdIntersection(entry) {
		if (!entry || !this.observedAds || !this.adObserver) return

		const adElement = entry.target
		const adid = adElement.getAttribute('data-ad-slot')
		if (entry.isIntersecting && adElement && !adElement.getAttribute('data-ad-status')) {
			console.log(`[AdMixin] ✅ 命中预加载 | Slot: ${adid}`)
			
			this.adObserver.unobserve(adElement)
			this.observedAds.delete(adElement)
			this.loadAd(adElement)
		}
	},

		async loadAd(adElement, retryCount = 0) {
			if (adElement.getAttribute('data-ad-status')) {
				return
			}

			// Only handle GPT Ads
			if (adElement.classList.contains('gpt-ad')) {
				this.displayGptAd(adElement, retryCount)
				return
			}
		},

		defineGptSlot(adElement) {
			const path = adElement.getAttribute('data-ad-slot')
			const id = adElement.id

			if (!path || !id) {
				console.warn('[AdMixin] GPT ad missing path or id', adElement)
				return
			}
			
			// Find size from config
			let sizes = [[300, 250], [336, 280]]; // default
			if (this.adsensConfig) {
				for (const key in this.adsensConfig) {
					const config = this.adsensConfig[key]
					if (config === path) continue; 
					if (Array.isArray(config)) {
						const match = config.find(c => c.path === path)
						if (match && match.sizes) sizes = match.sizes
					} else if (config.path === path && config.sizes) {
						sizes = config.sizes
					}
				}
			}

			window.googletag.cmd.push(() => {
				// Check if ID is already in use to prevent GPT errors
				const existingSlots = window.googletag.pubads().getSlots()
				const isIdUsed = existingSlots.some(s => s.getSlotElementId() === id)
				
				if (isIdUsed) {
					console.warn(`[AdMixin] Slot ID ${id} already defined, skipping definition.`)
					return
				}

				console.log(`[AdMixin] 📣 GPT Define Slot | Slot: ${path} | ID: ${id}`)
				const slot = window.googletag.defineSlot(path, sizes, id)
				if (slot) {
					slot.addService(window.googletag.pubads())
				}
			})
		},

		displayGptAd(adElement, retryCount) {
			if (!window.googletag) {
				if (retryCount < this.maxRetries) {
					setTimeout(() => this.displayGptAd(adElement, retryCount + 1), 1000)
					return
				}
				return
			}

			const id = adElement.id
			window.googletag.cmd.push(() => {
				console.log(`[AdMixin] 📣 GPT Display | ID: ${id}`)
				window.googletag.display(id)
				adElement.setAttribute('data-ad-status', 'loaded')
			})
		},

		cleanupAdSystem() {
			if (this.adObserver) {
				this.adObserver.disconnect()
				this.adObserver = null
			}
			if (this.domObserver) {
				this.domObserver.disconnect()
				this.domObserver = null
			}
			// Clean up GPT slots if needed?
			// window.googletag.destroySlots() // This would clear all slots. Be careful if sharing.
			this.observedAds.clear()
		},
	},
}
